import type {
  Coverage,
  CoverageState,
  DatasetBuilderProgress,
  StageId,
  StageRecord,
} from "./types";

export const STAGES: { id: StageId; label: string; description: string }[] = [
  {
    id: "columns",
    label: "Columns",
    description: "Schema optimization drafts the columns, pilots them, and locks them.",
  },
  {
    id: "search",
    label: "Search",
    description:
      "Config optimization labels a sample of papers and scores each revision of the search.",
  },
  {
    id: "master_list",
    label: "Master list",
    description: "The final search gives the papers that the dataset must cover.",
  },
  {
    id: "reading",
    label: "Reading documents",
    description: "Maps of at most 2,000 papers read every paper of the master list.",
  },
  {
    id: "coverage",
    label: "Coverage check",
    description: "Each paper of the master list gets a final state, and papers without rows are checked.",
  },
];

/** The approvals between the stages. The read of the master list has its own card. */
export const STAGE_APPROVALS: Partial<
  Record<StageId, { label: string; text: string; message: string }>
> = {
  columns: {
    label: "Approve the columns",
    text: "Schema optimization locked these columns. Approve them to continue the run plan, or ask the agent in the chat to change them.",
    message:
      "I approved the locked columns on the Datasets page. Continue with the next stage of the run plan.",
  },
  search: {
    label: "Approve the search",
    text: "Approve the best kept revision to make the master list, or ask the agent in the chat to change the search.",
    message:
      "I approved the search config on the Datasets page. Continue with the next stage of the run plan.",
  },
};

export const stageRecord = <S extends StageId>(
  progress: DatasetBuilderProgress | undefined,
  stage: S,
): StageRecord<S> | undefined =>
  progress?.stages.find((record): record is StageRecord<S> => record.stage === stage);

/**
 * The stage that is running or waiting, else the stage after the last recorded
 * one, else the last stage that is done. A skipped stage is never current.
 */
export const currentStage = (progress: DatasetBuilderProgress | undefined): StageId => {
  const open = progress?.stages.find(
    (record) => record.status === "running" || record.status === "waiting",
  );
  if (open) return open.stage;
  const recorded = STAGES.filter((stage) => stageRecord(progress, stage.id));
  const last = recorded.length ? STAGES.indexOf(recorded[recorded.length - 1]) : -1;
  if (last + 1 < STAGES.length) return STAGES[last + 1].id;
  const lastDone = [...STAGES].reverse().find(
    (stage) => stageRecord(progress, stage.id)?.status === "done",
  );
  return (lastDone ?? STAGES[STAGES.length - 1]).id;
};

/** What the user approves instead of the columns: the read of the master list. */
export type FullRead = {
  pilots: number;
  papers: number | null;
  precision: number | null;
  recall: number | null;
};

/** A full read to approve once schema optimization locked the columns, else null. */
export const fullReadOf = (progress: DatasetBuilderProgress | undefined): FullRead | null => {
  const columns = stageRecord(progress, "columns");
  if (!columns?.data.locked) return null;
  const kept = stageRecord(progress, "search")?.data.revisions.filter((item) => item.kept) ?? [];
  const best = kept[kept.length - 1];
  return {
    pilots: columns.data.pilots.length,
    papers: stageRecord(progress, "master_list")?.data.papers ?? null,
    precision: best?.precision ?? null,
    recall: best?.recall ?? null,
  };
};

/** The coverage states in the order of the bar, with their colors. */
export const COVERAGE_STATES: { id: CoverageState; label: string; className: string }[] = [
  { id: "in_grid", label: "Rows in the grid", className: "bg-emerald-600" },
  { id: "incoming", label: "Rows to write", className: "bg-emerald-300" },
  { id: "no_rows", label: "No rows", className: "bg-zinc-400" },
  { id: "screened_out", label: "Screened out", className: "bg-amber-400" },
  { id: "to_retry", label: "To retry", className: "bg-red-300" },
  { id: "failed", label: "Failed", className: "bg-red-600" },
  { id: "running", label: "Reading now", className: "bg-blue-400" },
  { id: "not_read", label: "Not read yet", className: "bg-zinc-100" },
];

/** The papers that a map has read, whatever the result. */
export const readPapers = (coverage: Coverage): number =>
  coverage.papers - (coverage.counts.not_read ?? 0) - (coverage.counts.running ?? 0);

export const percent = (part: number, whole: number): string =>
  whole ? `${Math.round((part / whole) * 100)}%` : "0%";

export const formatCount = (value: number): string => value.toLocaleString("en-US");
